import React, { useMemo } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { useAuthStore, useModalStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import CreditsRequiredDropdown from '@/components/Dropdowns/CreditsRequiredDropdown';
import useResponsiveness from '@/hooks/useResponsiveness';
import Tooltip from '@/components/Tooltip';
import { formatCreditBalance, resolveComposerCreditQuote } from '@/lib/formatCredits';

const createStyles = (colors) => StyleSheet.create({
  creditsContainer: {
    height: 34,
    minWidth: 34,
    marginRight: 4,
    paddingHorizontal: 8,
    borderRadius: 17,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  creditsContainerHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  triggerContainer: {
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
});

const ChatInputCreditsButtonTrigger = ({ isModal, onPress, hasInsufficientCredits, requestCost }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  return (
    <View style={styles.triggerContainer}>
      <Tooltip label="Estimated credits for this turn. Tap for a breakdown.">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Estimated ${requestCost} credits for this turn. Opens a breakdown.`}
          style={({ hovered }) => [
            styles.creditsContainer,
            hovered && styles.creditsContainerHovered,
          ]}
          onPress={isModal ? onPress : undefined}
        >
          <Icon 
            name="coins" 
            size={13} 
            color={hasInsufficientCredits ? colors.color_destructive : colors.text_color_primary}
          />
          {
            !!requestCost && (
            <Text 
              color={hasInsufficientCredits ? 'danger' : 'primary'}
              size='s'
            >
              ~{requestCost}
            </Text>
          )}
        </Pressable>
      </Tooltip>
    </View>
  );
};

function ChatInputCreditsButton({ requestCost, holdCost, fileEstimate = false }) {
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const isModal = Platform.OS !== 'web' || isXS;
  const totalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable);
  const { estimated, reserved } = resolveComposerCreditQuote(requestCost, holdCost);
    
  const hasInsufficientCredits = reserved > totalAvailableCredits;
  const displayEstimate = formatCreditBalance(estimated);

  const handleOnOpenModal = () => {
    openModal('CreditsRequiredModal', {
      requestCost: estimated,
      holdCost: reserved,
      fileEstimate,
    });
  };

  if (isModal) {
    return (
      <ChatInputCreditsButtonTrigger
        isModal={true}
        onPress={handleOnOpenModal}
        hasInsufficientCredits={hasInsufficientCredits}
        requestCost={displayEstimate}
      />
    );
  }

  return (
    <CreditsRequiredDropdown
      requestCost={estimated}
      holdCost={reserved}
      fileEstimate={fileEstimate}
      TriggerComponent={ChatInputCreditsButtonTrigger}
      triggerComponentProps={{ isModal, hasInsufficientCredits, requestCost: displayEstimate }}
    />
  )
  
} 

export default React.memo(ChatInputCreditsButton);
